"use client";

import { useMemo } from "react";
import { useWidgetEnv } from "../use-widget-env";
import type { MatchContext } from "./match";
import { useSessionContentContext } from "./session-content-context";
import { useHostLocation } from "./use-host-location";

/**
 * The audience every targeted surface (welcome, Home) matches against: the session kind
 * plus the host location, both replaceable by the private `audienceOverride` (matching only).
 * Stable until one of its parts changes.
 */
export function useMatchContext(): MatchContext {
  return useMatchContextFor(useSessionContentContext().visitors);
}

/**
 * Same audience, for a caller that holds the session kind itself (the shell is the provider of
 * the session context, so it cannot read it back from the context).
 */
export function useMatchContextFor(sessionVisitors: MatchContext["visitors"]): MatchContext {
  const { audienceOverride } = useWidgetEnv();
  const location = useHostLocation(audienceOverride);
  const visitors = audienceOverride?.visitors ?? sessionVisitors;
  return useMemo(
    () => ({ visitors, origin: location.origin, url: location.url }),
    [visitors, location],
  );
}
